<!--
  Capped height with a scrolling middle, and the actions pinned outside it.

  A screenshot preview makes this dialog taller than the viewport. Without this the Send button
  sits below the fold with no way to reach it, so a report with a screenshot cannot be submitted
  at all. Found in local testing, 22/09/2026.

  The preview image stays `w-full` with its natural aspect ratio on purpose: the redaction
  rectangles are stored in image pixels and converted through the element's bounding box, so
  anything that letterboxes the element (object-contain, a max-height) would silently misplace
  every redaction.
-->
<div class="w-[min(90vw,560px)] max-h-[85vh] flex flex-col overflow-hidden">
  <div class="px-6 pt-6 pb-3 shrink-0">
    <p class="text-[16px] font-bold text-[var(--base-text-alt-dark)] m-0 mb-1">Report a problem</p>
    <p class="text-sm text-[var(--tertiary-text)] m-0">
      Tell us what went wrong. We automatically include the page you were on, your browser and any
      recent errors, so there is no need to describe those.
    </p>
  </div>

  <form [formGroup]="form" class="flex flex-col gap-4 flex-1 min-h-0 overflow-y-auto px-6 pb-4">
    <div class="flex gap-2">
      <button type="button" (click)="form.controls.kind.setValue('bug')"
        [class.bg-[var(--primary-dark)]]="form.controls.kind.value === 'bug'"
        [class.text-white]="form.controls.kind.value === 'bug'"
        class="px-4 py-2 rounded-lg text-[13px] font-medium border border-[var(--app-base)] transition">
        Something is broken
      </button>
      <button type="button" (click)="form.controls.kind.setValue('feature')"
        [class.bg-[var(--primary-dark)]]="form.controls.kind.value === 'feature'"
        [class.text-white]="form.controls.kind.value === 'feature'"
        class="px-4 py-2 rounded-lg text-[13px] font-medium border border-[var(--app-base)] transition">
        I have a suggestion
      </button>
    </div>

    <div class="flex flex-col">
      <label for="bug-description" class="text-[13px] font-semibold text-[var(--base-text-alt-dark)] mb-1">
        What happened?
      </label>
      <textarea id="bug-description" formControlName="description" rows="5" [maxlength]="maxDescription"
        placeholder="What were you trying to do, and what did you see instead?"
        class="w-full rounded-lg border border-[var(--app-base)] p-3 text-[13px] resize-y focus:outline-none focus:border-[var(--primary-dark)]"></textarea>
      <div class="flex justify-between mt-1">
        <span class="text-[12px] text-[#C0392B]"
          *ngIf="form.controls.description.touched && form.controls.description.hasError('required')">
          Please describe the problem.
        </span>
        <span class="text-[12px] text-[var(--tertiary-text)] ml-auto">
          {{ form.controls.description.value.length }} / {{ maxDescription }}
        </span>
      </div>
    </div>

    <div class="bg-[var(--app-base)] rounded-lg px-4 py-4 flex flex-col gap-3">
      <div class="flex items-center gap-2 flex-wrap">
        <span class="text-[13px] font-semibold text-[var(--base-text-alt-dark)] mr-auto">Screenshot</span>
        <button type="button" (click)="capture()" [disabled]="capturing()"
          class="py-1.5 px-3 rounded-lg text-[12px] font-semibold bg-[var(--primary-dark)] text-white flex items-center gap-1.5 hover:opacity-90 disabled:opacity-60 transition">
          <span class="material-icons text-[16px]">photo_camera</span>
          {{ capturing() ? 'Choose a window…' : 'Capture screen' }}
        </button>
        <input type="file" accept="image/*" class="invisible absolute" #fileinput
          (change)="handleFileInput($any($event.target).files)" />
        <button type="button" (click)="openFileInput()"
          class="py-1.5 px-3 rounded-lg text-[12px] font-semibold border border-[var(--primary-dark)] text-[var(--primary-dark)] flex items-center gap-1.5 hover:opacity-90 transition">
          <span class="material-icons text-[16px]">attach_file</span> Attach image
        </button>
      </div>

      <p class="text-[12px] text-[var(--tertiary-text)] m-0" *ngIf="!preview()">
        Optional. Your browser will ask which window or tab to share.
      </p>

      <ng-container *ngIf="preview() as previewUrl">
        <p class="text-[12px] text-[var(--tertiary-text)] m-0">
          Use Edit to draw on it, add a note, or black out anything private.
        </p>

        <!--
          A thumbnail only. Drawing happens in the editor, where the image gets the whole window.
          draggable="false" stops the browser's native image drag, which otherwise fires on
          pointerdown and drags a ghost of the screenshot around.
        -->
        <img [src]="previewUrl" alt="Screenshot preview" draggable="false"
          class="w-full rounded border border-[var(--app-base)] select-none" />

        <div class="flex gap-2 flex-wrap">
          <button type="button" (click)="editScreenshot()"
            class="py-1.5 px-3 rounded-lg text-[12px] font-semibold bg-[var(--primary-dark)] text-white flex items-center gap-1.5 hover:opacity-90 transition">
            <span class="material-icons text-[16px]">edit</span> Edit
          </button>
          <button type="button" (click)="removeScreenshot()"
            class="py-1.5 px-3 rounded-lg text-[12px] font-medium text-[var(--base-text-alt-dark)] hover:bg-white transition">
            Remove screenshot
          </button>
        </div>
      </ng-container>
    </div>
  </form>

  <div class="flex justify-end gap-3 px-6 py-4 shrink-0 border-t border-[var(--app-base)]">
    <button type="button" (click)="dialogRef.close()" [disabled]="submitting()"
      class="px-5 py-2.5 rounded-lg text-[13px] font-medium text-[var(--base-text-alt-dark)] hover:bg-[var(--app-base)] disabled:opacity-40 transition">
      Cancel
    </button>
    <button type="button" (click)="submit()" [disabled]="submitting()"
      class="px-5 py-2.5 rounded-lg text-[13px] font-medium bg-[var(--primary-dark)] text-white hover:opacity-90 disabled:bg-[#E6E6E6] disabled:text-[#C3C3C3] transition">
      {{ submitting() ? 'Sending…' : 'Send report' }}
    </button>
  </div>
</div>
